/**
 * PackView - Vista principal de Checklists
 *
 * Vista para prepararse para salir de casa con diferentes escenarios.
 * Diseñado para ADHD: visual claro, progreso visible, items críticos destacados.
 */

import { useEffect, useMemo } from 'react';
import { useChecklistStore } from '@/store';
import { ScenarioTabs } from './ScenarioTabs';
import { ProgressHeader } from './ProgressHeader';
import { CategoryGroup } from './CategoryGroup';
import { AddItemForm } from './AddItemForm';
import type { ChecklistItem, ItemCategory } from '@/core/types';

const CATEGORY_ORDER: ItemCategory[] = [
  'health',
  'personal',
  'tech',
  'hygiene',
  'clothing',
  'work',
  'other',
];

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  health: 'Salud',
  personal: 'Personal',
  tech: 'Tech',
  hygiene: 'Higiene',
  clothing: 'Ropa',
  work: 'Trabajo',
  other: 'Otros',
};

export function PackView() {
  const initialize = useChecklistStore((state) => state.initialize);
  const checklistsRaw = useChecklistStore((state) => state.checklists);
  const activeId = useChecklistStore((state) => state.activeChecklistId);

  // Ordenar checklists con useMemo para evitar re-renders
  const checklists = useMemo(
    () => [...checklistsRaw].sort((a, b) => a.order - b.order),
    [checklistsRaw]
  );

  // Encontrar checklist activo
  const activeChecklist = useMemo(
    () => checklistsRaw.find((c) => c.id === activeId),
    [checklistsRaw, activeId]
  );

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Group items by category
  const groupedItems = useMemo((): Partial<Record<ItemCategory, ChecklistItem[]>> => {
    if (!activeChecklist) return {};

    const sorted = [...activeChecklist.items].sort((a, b) => {
      // Critical items first within each category
      if (a.priority === 'critical' && b.priority !== 'critical') return -1;
      if (a.priority !== 'critical' && b.priority === 'critical') return 1;
      return a.order - b.order;
    });

    const result: Partial<Record<ItemCategory, ChecklistItem[]>> = {};
    sorted.forEach((item) => {
      if (!result[item.category]) result[item.category] = [];
      result[item.category]?.push(item);
    });
    return result;
  }, [activeChecklist]);

  if (!activeChecklist) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-slate-400">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Scenario Tabs */}
      <ScenarioTabs checklists={checklists} activeId={activeId} />

      {/* Progress Header */}
      <ProgressHeader checklist={activeChecklist} />

      {/* Items by Category */}
      <div className="space-y-4">
        {CATEGORY_ORDER.map((category) => {
          const items = groupedItems[category];
          if (!items || items.length === 0) return null;

          return (
            <CategoryGroup
              key={category}
              category={category}
              label={CATEGORY_LABELS[category]}
              items={items}
              checklistId={activeChecklist.id}
            />
          );
        })}
      </div>

      {/* Empty state */}
      {activeChecklist.items.length === 0 && (
        <div className="text-center py-8">
          <p className="text-slate-500 mb-4">
            No hay items en este checklist.
          </p>
        </div>
      )}

      {/* Add Item */}
      <AddItemForm checklistId={activeChecklist.id} />
    </div>
  );
}
